/**
 * Browser lifecycle wiring for the coach and runner apps.
 *
 * Guarded by a typeof check rather than assumed, so importing this module from
 * a bundle that also runs under Node (CRA's test runner, SSR) is inert rather
 * than fatal.
 */

import type { UsageTracker, UsageSessionStore } from './tracker';

/**
 * Pauses the tracker while the tab is hidden and resumes it on return.
 *
 * `visibilitychange` is the load-bearing one: browsers throttle timers in
 * background tabs, so without this a minimised window would keep accruing
 * time in long, unreliable gaps. `pagehide` additionally covers the
 * back/forward cache, where a tab is frozen without ever firing unload.
 *
 * Returns a teardown function.
 */
export function attachWebLifecycle(tracker: UsageTracker): () => void {
  if (typeof document === 'undefined') return () => {};

  const onVisibility = () => {
    if (document.hidden) tracker.pause();
    else tracker.resume();
  };
  const onHide = () => tracker.pause();
  const onShow = () => tracker.resume();

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onHide);
  window.addEventListener('pageshow', onShow);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onHide);
    window.removeEventListener('pageshow', onShow);
  };
}

/**
 * Session storage backed by sessionStorage.
 *
 * sessionStorage, not localStorage, on purpose. It survives an in-tab reload
 * and every client-side route change, but a second tab gets its own store —
 * which is correct, because a second tab IS a second app open — and closing
 * the tab drops it. localStorage would stitch two tabs into one session and
 * resurrect a session days later.
 *
 * Every access is wrapped: Safari in private mode and browsers set to block
 * site data throw on access rather than returning null. A tracker with no
 * usable store simply keeps the id in memory.
 */
export function createWebSessionStore(key = 'rhwb-usage-session'): UsageSessionStore {
  return {
    read() {
      try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return typeof parsed?.id === 'string' && typeof parsed?.email === 'string'
          ? { id: parsed.id, email: parsed.email }
          : null;
      } catch {
        return null;
      }
    },
    write(value) {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
      } catch {
        /* storage unavailable — the id stays in memory for this page load */
      }
    },
    clear() {
      try {
        sessionStorage.removeItem(key);
      } catch {
        /* nothing to do */
      }
    },
  };
}
